import React, { useState } from "react";
import { graphql, Link } from "gatsby";

import {
    fitsPage,
    pageShell,
    quizPageShell,
    sectionEyebrow,
    primaryCta,
    quizSection,
    quizIntro,
    quizBadges,
    quizProgressMeta,
    quizProgress,
    quizProgressFill,
    quizCard,
    quizQuestionNumber,
    quizQuestionTitle,
    quizQuestionHint,
    quizAnswers,
    quizAnswer,
    quizAnswerSelected,
    quizNav,
    quizBack,
    quizPrivacy,
    quizResult,
    quizResultHeader,
    quizResultEyebrow,
    quizResultTitle,
    quizConsistency,
    quizResultGrid,
    quizResultImage,
    quizResultCopy,
    quizRecommendations,
    quizScores,
    quizScoreRow,
    quizScoreBar,
    quizResultActions,
    quizRestart,
} from "./what-fits-you.module.scss";

import hourglassImage from "../../assets/images/what-fits/reference/figure-pear-reference.jpg";
import pearImage from "../../assets/images/what-fits/reference/figure-pear-20260722.jpg";
import appleImage from "../../assets/images/what-fits/reference/figure-apple-reference.jpg";
import rectangleImage from "../../assets/images/what-fits/reference/figure-rectangle-reference.jpg";
import triangleImage from "../../assets/images/what-fits/reference/figure-inverted-triangle-reference.jpg";
import { IPageBase } from "../../models/page-base.model";
import { ISite } from "../../models/site.model";
import usePageMetadata from "../../hooks/use-page-metadata";
import { FIGURE_TYPES, QUESTIONS, RESULT_DATA, calculateFigureType } from "../../utils/figure-analysis";
import MainLayout from "../../layouts/main-layout";
import StaticPageNav from "../organisms/static-page-nav";
import MinimalFooter from "../organisms/minimal-footer";

interface IFigureQuizProps extends IPageBase {
    readonly data: { site: ISite };
}

const figureResultData: Record<string, any> = RESULT_DATA;

const figureResultImages: Record<string, string> = {
    hourglass: hourglassImage,
    pear: pearImage,
    apple: appleImage,
    rectangle: rectangleImage,
    "inverted-triangle": triangleImage,
};

const FigureQuiz: React.FC<IFigureQuizProps> = ({ data, pageContext }) => {
    const { pageTitle, defaultStructuredData } = usePageMetadata(pageContext, data.site);
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState<string[]>([]);
    const [result, setResult] = useState<any>(null);
    const question = QUESTIONS[step];
    const progress = result ? 100 : Math.round(((step + 1) / QUESTIONS.length) * 100);

    const scrollToQuiz = () => {
        if (typeof window !== "undefined") {
            window.requestAnimationFrame(() => document.getElementById("quiz-figury")?.scrollIntoView({ behavior: "smooth", block: "start" }));
        }
    };

    const chooseAnswer = (answerId: string) => {
        const nextAnswers = answers.slice(0, step);
        nextAnswers[step] = answerId;
        setAnswers(nextAnswers);
        if (step === QUESTIONS.length - 1) {
            setResult(calculateFigureType(nextAnswers));
        } else {
            setStep(step + 1);
        }
        scrollToQuiz();
    };

    const goBack = () => {
        if (step === 0) return;
        setResult(null);
        setStep(step - 1);
        scrollToQuiz();
    };

    const restartQuiz = () => {
        setStep(0);
        setAnswers([]);
        setResult(null);
        scrollToQuiz();
    };

    return (
        <MainLayout
            className={fitsPage}
            HeaderComponent={null}
            footer={false}
            SEOProps={{ title: pageTitle, structuredData: defaultStructuredData }}
        >
            <StaticPageNav menuId="figure-quiz-mobile-menu" />

            <div className={`${pageShell} ${quizPageShell}`}>
                <section className={quizSection} id="quiz-figury" aria-labelledby={result ? "figure-result-title" : "figure-quiz-title"}>
                    <header className={quizIntro}>
                        <p className={sectionEyebrow}>BEZPŁATNY QUIZ FASHION FAIRY</p>
                        <h1 id="figure-quiz-title">Poznaj swój typ figury</h1>
                        <p>Odpowiedz na 6 prostych pytań o proporcje. Otrzymasz orientacyjny wynik i fasony, od których warto zacząć.</p>
                        <ul className={quizBadges} aria-label="Informacje o quizie">
                            <li><strong>6</strong><span>krótkich pytań</span></li>
                            <li><strong>2</strong><span>minuty</span></li>
                            <li><strong>0</strong><span>zdjęć i logowania</span></li>
                        </ul>
                    </header>

                    <div className={quizProgressMeta} aria-live="polite">
                        <span>{result ? "Analiza zakończona" : `Pytanie ${step + 1} z ${QUESTIONS.length}`}</span>
                        <strong>{progress}%</strong>
                    </div>
                    <div className={quizProgress} aria-hidden="true">
                        <span className={quizProgressFill} style={{ width: `${progress}%` }} />
                    </div>

                    {!result && question && (
                        <div className={quizCard}>
                            <p className={quizQuestionNumber}>Krok {step + 1}</p>
                            <h2 className={quizQuestionTitle}>{question.title}</h2>
                            <p className={quizQuestionHint}>{question.hint}</p>
                            <div className={quizAnswers} role="group" aria-label={question.title}>
                                {question.answers.map((answer: any) => (
                                    <button
                                        className={`${quizAnswer} ${answers[step] === answer.id ? quizAnswerSelected : ""}`}
                                        type="button"
                                        key={answer.id}
                                        onClick={() => chooseAnswer(answer.id)}
                                        aria-pressed={answers[step] === answer.id}
                                    >
                                        <span>{answer.label}</span><b aria-hidden="true">→</b>
                                    </button>
                                ))}
                            </div>
                            <div className={quizNav}>
                                <button className={quizBack} type="button" onClick={goBack} disabled={step === 0}>← Poprzednie pytanie</button>
                                <p className={quizPrivacy}>Odpowiedzi pozostają wyłącznie w tej przeglądarce.</p>
                            </div>
                        </div>
                    )}

                    {result && (
                        <div className={quizResult} aria-live="polite">
                            <header className={quizResultHeader}>
                                <div>
                                    <p className={quizResultEyebrow}>Twój wynik</p>
                                    <h2 className={quizResultTitle} id="figure-result-title">Twój typ figury to <em>{result.name}</em></h2>
                                    <p>Najbliższy typ uzupełniający: <strong>{figureResultData[result.runnerUp].name}</strong></p>
                                </div>
                                <div className={quizConsistency}><strong>{result.consistency}%</strong><span>spójności<br />odpowiedzi</span></div>
                            </header>

                            <div className={quizResultGrid}>
                                <div className={quizResultImage}>
                                    <img src={figureResultImages[result.type]} alt={`Przykład sylwetki: ${result.name.toLowerCase()}`} />
                                </div>
                                <div className={quizResultCopy}>
                                    <p className={quizResultEyebrow}>O figurze</p>
                                    <h3>{result.tone}</h3>
                                    <p>{result.summary}</p>
                                    <h3>Stylizacje, które pokochasz</h3>
                                    <ul className={quizRecommendations}>
                                        {result.bestFits.map((item: string) => <li key={item}>✓ {item}</li>)}
                                    </ul>
                                    <p><strong>Zwróć uwagę:</strong> {result.watchOut}</p>
                                </div>
                            </div>

                            <details className={quizScores}>
                                <summary>Zobacz rozkład punktów</summary>
                                {FIGURE_TYPES.map((type: string) => {
                                    const maxScore = Math.max(...FIGURE_TYPES.map((key: string) => result.scores[key]), 1);
                                    return (
                                        <div className={quizScoreRow} key={type}>
                                            <span>{figureResultData[type].name}</span>
                                            <div><i className={quizScoreBar} style={{ width: `${Math.round((result.scores[type] / maxScore) * 100)}%` }} /></div>
                                            <strong>{result.scores[type]}</strong>
                                        </div>
                                    );
                                })}
                            </details>

                            <div className={quizResultActions}>
                                <Link className={primaryCta} to="/okresl-figure/#figure-types-title">WRÓĆ DO PORADNIKA FIGURY →</Link>
                                <button className={quizRestart} type="button" onClick={restartQuiz}>Zacznij quiz od nowa</button>
                            </div>
                            <p className={quizPrivacy}>Wynik jest orientacyjną analizą proporcji na podstawie Twoich odpowiedzi. Każda sylwetka jest indywidualna i może łączyć cechy kilku typów.</p>
                        </div>
                    )}
                </section>
            </div>

            <MinimalFooter />
        </MainLayout>
    );
};

export const query = graphql`
    query($language: String!) {
        locales: allLocale(filter: { language: { eq: $language } }) {
            edges { node { ns data language } }
        }
        site { ...siteFields }
    }
`;

export default FigureQuiz;
